import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { CompletionScreen } from "@/components/CompletionScreen";
import { DayLoader } from "@/components/DayLoader";
import { ProgressBar } from "@/components/ProgressBar";
import { Pronounce } from "@/components/Pronounce";
import { useSettings } from "@/hooks/useSettings";
import { pad } from "@/lib/format";
import { gradeFromCorrect, review, studyQueue } from "@/lib/study";
import type { DayData, Word } from "@/types/lex";

type FieldState = "idle" | "correct" | "wrong";

export function Typing() {
  const { day: dayParam } = useParams<{ day: string }>();
  const day = Number(dayParam) || 1;
  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} data={data} />}
    </DayLoader>
  );
}

function Body({ day, data }: { day: number; data: DayData }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [queue, setQueue] = useState<readonly Word[]>(() =>
    studyQueue(day, data.words, settings.roundSize)
  );
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [state, setState] = useState<FieldState>("idle");
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [reveal, setReveal] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // After a wrong answer we briefly lock advancement so a stray second Enter
  // (key-repeat on desktop, a double-tap of the soft-keyboard "Go" on mobile)
  // can't skip the word before the learner has read the correct spelling.
  const advanceLockRef = useRef(false);
  const w = queue[i];

  useEffect(() => {
    inputRef.current?.focus();
  }, [i]);

  function check() {
    if (!w) return;
    const right = val.trim().toLowerCase() === w.word.toLowerCase();
    review(day, w.index, gradeFromCorrect(right));
    if (right) {
      setState("correct");
      setCorrect((c) => c + 1);
      if (settings.autoNext) setTimeout(next, 700);
    } else {
      setState("wrong");
      setWrong((x) => x + 1);
      // Reveal the correct spelling in the blanks (which sit above the input,
      // so they stay visible even with the mobile keyboard open) and lock
      // advancement so the learner can actually see what they missed.
      setReveal(true);
      advanceLockRef.current = true;
      window.setTimeout(() => {
        advanceLockRef.current = false;
      }, 900);
    }
  }

  function next() {
    setVal("");
    setState("idle");
    setReveal(false);
    if (i + 1 >= queue.length) setDone(true);
    else setI(i + 1);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    // Pressing Enter after the answer has been judged moves forward — pressing
    // it again on an already-correct entry must not double-count the score.
    // While the post-wrong lock is active, ignore Enter so the answer stays
    // on screen instead of being skipped past.
    if (state === "idle") check();
    else if (!advanceLockRef.current) next();
  }

  function restart() {
    setQueue(studyQueue(day, data.words, settings.roundSize));
    setI(0);
    setVal("");
    setState("idle");
    setReveal(false);
    setCorrect(0);
    setWrong(0);
    setDone(false);
  }

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(day)}`, to: `/day/${day}` },
            { label: "Typing" },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="row-s" style={{ marginBottom: "var(--s-5)" }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="t-eyebrow">Mode M03 · Dictation</div>
            <h1 className="t-display" style={{ fontSize: "var(--t-2xl)", margin: 0 }}>
              Spell the <span className="italic">English word</span>.
            </h1>
          </div>
          <div className="row-c" style={{ gap: 16 }}>
            <ScorePill dot="moss" value={correct} />
            <ScorePill dot="red" value={wrong} />
            <button className="btn btn-sm" onClick={() => navigate(`/day/${day}`)} type="button">Exit</button>
          </div>
        </div>
        <div className="row-c" style={{ gap: 12, marginBottom: "var(--s-6)" }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={done ? queue.length : i} max={queue.length} />
          </div>
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {pad(i + 1)} / {pad(queue.length)}
          </div>
        </div>

        {!done && w && (
          <div className="typing-stage">
            <div className="row-c" style={{ justifyContent: "center", gap: 14, marginBottom: "var(--s-3)" }}>
              <Pronounce word={w.word} size={48} />
            </div>
            <div className="typing-ipa">/{w.ipa}/</div>
            <div className="typing-meaning">
              {w.meaning_vi} · <span style={{ fontStyle: "normal" }}>{w.pos}</span>
            </div>
            <div className="typing-blanks">
              <Blanks target={w.word} input={val} reveal={reveal} />
            </div>
            <input
              ref={inputRef}
              className={`typing-input ${state === "correct" ? "is-correct" : ""} ${
                state === "wrong" ? "is-wrong" : ""
              }`}
              type="text"
              value={val}
              onChange={(e) => {
                setVal(e.target.value);
                if (state !== "idle") {
                  setState("idle");
                  // Editing means a fresh attempt — release the skip lock.
                  advanceLockRef.current = false;
                }
              }}
              onKeyDown={onKey}
              autoFocus
              spellCheck={false}
              placeholder="…"
              autoComplete="off"
              aria-label="Type the English word"
            />
            {state === "wrong" && (
              <p
                className="t-display italic typing-answer"
                style={{ fontSize: "var(--t-lg)", color: "var(--accent-red)", marginTop: "var(--s-4)" }}
              >
                Correct spelling:{" "}
                <span style={{ fontStyle: "normal", color: "var(--text)", borderBottom: "1.5px solid var(--ink-0)" }}>
                  {w.word}
                </span>
              </p>
            )}
            <div
              className="row-c"
              style={{ justifyContent: "center", gap: 12, marginTop: "var(--s-5)", flexWrap: "wrap" }}
            >
              <button className="btn btn-sm" onClick={() => setReveal((r) => !r)} type="button">
                {reveal ? "Hide hint" : "Reveal hint"}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => {
                  // Owning up counts as a miss: it's the clearest signal the
                  // schedule can get, so grade it rather than letting the word
                  // slip through ungraded.
                  if (w && state === "idle") review(day, w.index, "again");
                  setReveal(true);
                  setState("wrong");
                }}
                type="button"
              >
                I don't know
              </button>
              {(state === "wrong" || (state === "correct" && !settings.autoNext)) && (
                <button className="btn btn-solid btn-sm" onClick={next} type="button">Next →</button>
              )}
            </div>
          </div>
        )}

        {done && (
          <CompletionScreen
            title={
              <>
                <span className="t-num">{correct}</span>
                <span style={{ color: "var(--text-muted)" }}> / </span>
                {queue.length}
              </>
            }
            subtitle={`Accuracy: ${Math.round((correct / queue.length) * 100)}%`}
            stats={[
              { label: "Correct", value: correct, dot: "moss" },
              { label: "Wrong", value: wrong, dot: "red" },
            ]}
            primaryAction={{ label: "Back to day →", onClick: () => navigate(`/day/${day}`) }}
            secondaryAction={{ label: "↻ Try again", onClick: restart }}
          />
        )}
      </section>
    </div>
  );
}

function ScorePill({ dot, value }: { dot: "moss" | "red"; value: number }) {
  return (
    <span
      className="t-mono"
      style={{ fontSize: 12, color: dot === "moss" ? "var(--accent-moss)" : "var(--accent-red)" }}
    >
      <span className={`dot dot--${dot}`} style={{ marginRight: 6 }} />
      {value}
    </span>
  );
}

function Blanks({ target, input, reveal }: { target: string; input: string; reveal: boolean }) {
  return (
    <>
      {target.split("").map((c, k) => {
        const inputChar = input[k];
        const isSpace = c === " ";
        let cls = "typing-blanks__char";
        let display: string = "_";
        if (isSpace) {
          cls += " is-space";
          display = " ";
        } else if (reveal) {
          cls += " is-filled";
          display = c;
        } else if (inputChar !== undefined) {
          if (inputChar.toLowerCase() === c.toLowerCase()) {
            cls += " is-filled";
            display = inputChar;
          } else {
            cls += " is-wrong";
            display = inputChar;
          }
        } else if (k === 0) {
          cls += " is-filled";
          display = c;
        }
        return (
          <span key={k} className={cls}>
            {display}
          </span>
        );
      })}
    </>
  );
}
